import { CheckCircle2 } from 'lucide-react';
import type { ProjectProgress, ProgressTab } from '../lib/projectProgress';

interface ProjectProgressBarProps {
  progress: ProjectProgress;
  /** Jump to the tab owning the first outstanding item. */
  onNavigate: (tab: ProgressTab) => void;
}

export function ProjectProgressBar({ progress, onNavigate }: ProjectProgressBarProps) {
  const { done, total, percent, complete, remaining } = progress;
  const next = remaining[0];

  return (
    <div
      className="bg-white border-b border-gray-200 px-3 sm:px-6 lg:px-8 py-2"
      data-testid="project-progress-bar"
    >
      <div className="max-w-7xl mx-auto flex items-center gap-3">
        <span
          className="text-xs sm:text-sm font-medium text-gray-900 whitespace-nowrap"
          data-testid="progress-count"
        >
          {complete ? (
            <span className="flex items-center gap-1.5 text-green-700">
              <CheckCircle2 className="w-4 h-4" />
              All required fields complete
            </span>
          ) : (
            <>
              {done} of {total} <span className="hidden sm:inline">required fields</span>
            </>
          )}
        </span>

        <div
          className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden min-w-[40px]"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Required fields complete"
        >
          <div
            className={`h-full rounded-full transition-all duration-300 ${complete ? 'bg-green-600' : 'bg-blue-600'}`}
            style={{ width: `${percent}%` }}
          />
        </div>

        {!complete && next && (
          <button
            type="button"
            onClick={() => onNavigate(next.tab)}
            data-testid="progress-next"
            className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
            title={`Go to ${next.label}`}
          >
            <span className="hidden sm:inline">Next: {next.label}</span>
            <span className="sm:hidden">{total - done} left</span>
          </button>
        )}
      </div>
    </div>
  );
}
